/**
 * Глобальный обработчик ошибок API.
 * Централизованное логирование и показ toast-уведомлений.
 */

import LifeBridge from '@tnlife/tn-bridge';

// Типы для toast (совместимо с uikit toaster)
type ToastType = 'error' | 'info' | 'success' | 'warning';

interface ToastOptions {
  type?: ToastType;
  duration?: number;
}

// Глобальный toast callback (устанавливается при инициализации приложения)
let globalToastCallback: ((message: string, options?: ToastOptions) => void) | null = null;

/**
 * Установить глобальный toast callback.
 * Вызывается при инициализации приложения для подключения toast-системы.
 */
export function setGlobalToastCallback(
  callback: (message: string, options?: ToastOptions) => void
): void {
  globalToastCallback = callback;
}

/**
 * Показать toast-уведомление.
 */
export function showToast(message: string, options?: ToastOptions): void {
  if (globalToastCallback) {
    globalToastCallback(message, options);
  } else {
    // Fallback на console если toast не инициализирован
    console.warn('[Toast]', message);
  }
}

/**
 * Сообщения для типовых HTTP ошибок.
 */
const ERROR_MESSAGES: Record<number, string> = {
  400: 'Некорректные данные',
  403: 'Недостаточно прав для этого действия',
  404: 'Не найдено',
  409: 'Конфликт. Возможно, данные были изменены.',
  422: 'Ошибка валидации',
  429: 'Слишком много запросов. Попробуйте позже.',
  500: 'Произошла ошибка. Попробуйте позже.',
  502: 'Сервис временно недоступен',
  503: 'Сервис временно недоступен',
  504: 'Сервис временно недоступен',
};

/**
 * Достаёт человекочитаемый текст из тела ответа бэкенда ({"error":"…"}).
 * Возвращает undefined, если тело не JSON или поля error нет.
 */
function serverMessage(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const trimmed = raw.trim();
  if (!trimmed.startsWith('{')) return undefined;
  try {
    const parsed = JSON.parse(trimmed);
    const text = typeof parsed?.error === 'string' ? parsed.error.trim() : '';
    return text || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Текст ошибки API для показа пользователю: сначала message от бэкенда,
 * затем словарь по HTTP-статусу, затем fallback. Нужен там, где ошибка
 * рисуется в самом компоненте (модалки), а не тостом — иначе в интерфейс
 * попадает сырое тело ответа вида {"error":"…"}.
 */
export function apiErrorMessage(error: unknown, fallback = 'Произошла ошибка'): string {
  if (!(error instanceof Error)) return fallback;
  const status = (error as any).status;
  return serverMessage(error.message) ?? ERROR_MESSAGES[status] ?? error.message ?? fallback;
}

/**
 * Текст ошибки ЗАГРУЗКИ сущности: для 404/403 — контекстная формулировка
 * (генерик «Не найдено» в пустом экране выглядит как поломка), для прочих —
 * общий словарь. Сырые «ApiError: not found» до пользователя не доходят.
 */
export function loadErrorMessage(
  error: unknown,
  ctx: { notFound: string; forbidden: string; fallback: string }
): string {
  const status = error instanceof Error ? (error as any).status : undefined;
  if (status === 404) return ctx.notFound;
  if (status === 403) return ctx.forbidden;
  return apiErrorMessage(error, ctx.fallback);
}

/**
 * Обработать ошибку API.
 * Логирует ошибку и показывает toast-уведомление.
 */
export function handleError(error: Error, url: string): void {
  // Извлекаем статус из ошибки
  const status = (error as any).status;
  
  // Логирование
  console.error(`[API Error] ${url}`, {
    status,
    message: error.message,
    name: error.name,
  });

  // Осмысленный текст от бэкенда важнее словарного: «сузьте запрос к ИИ»
  // полезнее, чем «Ошибка валидации».
  showToast(apiErrorMessage(error), { type: 'error', duration: 5000 });
}

/**
 * Обработать 401 Unauthorized.
 * Перенаправляет на страницу логина с сохранением текущего URL.
 */
export function handleUnauthorized(): void {
  if (LifeBridge.isIFrame || LifeBridge.isWebView) {
    console.warn('[Auth] Unauthorized in TN Life miniapp, skipping profile redirect');
    showToast('Не удалось авторизоваться через TN Life', { type: 'error', duration: 5000 });
    return;
  }

  const currentPath = window.location.pathname + window.location.search;
  const returnTo = encodeURIComponent(currentPath);
  
  console.warn('[Auth] Unauthorized, redirecting to login');
  
  // Redirect напрямую на backend login endpoint
  // Backend в dev mode создаст сессию и вернет нас обратно
  window.location.href = `/auth/login?return=${returnTo}`;
}
